<template>
  <header class="header" :class="{ 'scrolled': isScrolled }" ref="header" @keydown.esc="closeMenu">
    <div class="nav-container">
      <div class="logo">
        <img src="@/assets/branding/logo-black.svg" alt="Singing Molenbeek Logo" />
      </div>
      
      <!-- Desktop Nav -->
      <nav class="desktop-nav">
        <ul>
          <li><a href="#hero">{{ $t('navbar.home') }}</a></li>
          <li><a href="#about">{{ $t('navbar.about') }}</a></li>
          <li><a href="#donate">{{ $t('navbar.donate') }}</a></li>
          <li><a href="#posts">{{ $t('navbar.news') }}</a></li>
          <li><a href="#gallery">{{ $t('navbar.gallery') }}</a></li>
          <li><a href="#contact" class="cta-btn">{{ $t('navbar.contact') }}</a></li>
          <li class="locale-wrapper"><LocaleSwitcher /></li>
        </ul>
      </nav>

      <!-- Mobile Menu Icon -->
      <button
        type="button"
        class="menu-icon"
        :class="{ 'active': isMenuOpen }"
        :aria-label="$t(isMenuOpen ? 'navbar.closeMenu' : 'navbar.openMenu')"
        :aria-expanded="isMenuOpen"
        aria-controls="mobile-menu"
        @click="toggleMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- Mobile Nav Overlay -->
      <nav id="mobile-menu" class="mobile-nav" :class="{ 'open': isMenuOpen }" :aria-hidden="!isMenuOpen">
        <ul>
          <li><a href="#hero" @click="closeMenu">{{ $t('navbar.home') }}</a></li>
          <li><a href="#about" @click="closeMenu">{{ $t('navbar.about') }}</a></li>
          <li><a href="#donate" @click="closeMenu">{{ $t('navbar.donate') }}</a></li>
          <li><a href="#posts" @click="closeMenu">{{ $t('navbar.news') }}</a></li>
          <li><a href="#gallery" @click="closeMenu">{{ $t('navbar.gallery') }}</a></li>
          <li><a href="#contact" @click="closeMenu">{{ $t('navbar.contact') }}</a></li>
          <li><LocaleSwitcher /></li>
        </ul>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import LocaleSwitcher from './LocaleSwitcher.vue';

const isMenuOpen = ref(false);
const isScrolled = ref(false);
const header = ref(null);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  if(isMenuOpen.value) {
    document.body.style.overflow = 'hidden'; // Prevent scrolling when menu is open
  } else {
    document.body.style.overflow = '';
  }
};

const closeMenu = () => {
  isMenuOpen.value = false;
  document.body.style.overflow = '';
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.body.style.overflow = '';
});
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 0.55rem 1.5rem;
  border-bottom: 1px solid rgba(38, 57, 77, 0.07);
  background-color: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);
  transition: padding 0.25s ease, box-shadow 0.25s ease;
}

.header.scrolled {
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
  box-shadow: 0 8px 28px rgba(38, 57, 77, 0.09);
}

.nav-container {
  max-width: var(--content-width);
  min-height: 62px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo img {
  display: block;
  height: 3.15rem;
  transition: height 0.25s ease;
}

.header.scrolled .logo img {
  height: 2.7rem;
}

/* Desktop Nav */
.desktop-nav ul {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: center;
  gap: clamp(1rem, 2vw, 1.8rem);
}

.desktop-nav a {
  text-decoration: none;
  color: var(--secondary-color);
  font-weight: 650;
  font-size: 0.94rem;
  transition: color 0.2s;
  position: relative;
}

.desktop-nav a:not(.cta-btn):hover {
  color: var(--primary-color);
}

.desktop-nav a:not(.cta-btn)::after {
  content: '';
  position: absolute;
  bottom: -7px;
  left: 0;
  width: 0;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  transition: width 0.3s ease;
}

.desktop-nav a:not(.cta-btn):hover::after {
  width: 100%;
}

/* Contact Button Style */
.cta-btn {
  background-color: var(--primary-color);
  color: white !important;
  padding: 0.64rem 1.25rem;
  border-radius: 50px;
  box-shadow: 0 7px 18px rgba(201, 59, 69, 0.2);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease !important;
}

.cta-btn:hover {
  background-color: var(--primary-dark);
  box-shadow: 0 10px 24px rgba(201, 59, 69, 0.28);
  transform: translateY(-1px);
}

/* Mobile Menu Icon */
.menu-icon {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  cursor: pointer;
  z-index: 1002;
  padding: 0;
  border: 0;
  background: transparent;
}

.menu-icon span {
  display: block;
  width: 100%;
  height: 3px;
  background-color: var(--secondary-color);
  border-radius: 3px;
  transition: all 0.3s ease;
}

.menu-icon.active span:nth-child(1) {
  transform: translateY(9px) rotate(45deg);
}

.menu-icon.active span:nth-child(2) {
  opacity: 0;
}

.menu-icon.active span:nth-child(3) {
  transform: translateY(-9px) rotate(-45deg);
}

/* Mobile Nav Overlay */
.mobile-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background:
    radial-gradient(circle at 15% 15%, rgba(201, 59, 69, 0.1), transparent 30%),
    var(--bg-color);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.mobile-nav.open {
  opacity: 1;
  visibility: visible;
}

.mobile-nav ul {
  list-style: none;
  padding: 0;
  text-align: center;
}

.mobile-nav li {
  margin: 1.65rem 0;
}

.mobile-nav a {
  text-decoration: none;
  color: var(--secondary-color);
  font-size: 1.45rem;
  font-weight: 750;
}

@media (max-width: 900px) {
  .desktop-nav {
    display: none;
  }
  .menu-icon {
    display: flex;
  }
  .header {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .logo img {
    height: 2.85rem;
  }
}
</style>
